import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@vtex/admin-ui'
import { useIntl } from 'react-intl'

import { usePromotionArchive } from 'hooks'
import { useAnalytics } from 'use-analytics'

export function usePromotionState() {
  const intl = useIntl()
  const showToast = useToast()
  const { track } = useAnalytics()

  const queryClient = useQueryClient()
  const { update } = usePromotionArchive()

  const updateState = ({
    id,
    archive,
    allowUndo = true,
  }: {
    id: string
    archive: boolean
    allowUndo?: boolean
  }) => {
    allowUndo !== false &&
      track(archive ? 'Promotion Archived' : 'Promotion Unarchived')

    return update(
      {
        id,
        archive,
      },
      {
        onSuccess: (response: Response) => {
          if (!response.ok) throw new Error()

          showToast({
            variant: 'positive',
            message: intl.formatMessage(
              {
                id: 'admin/promotions.list.actions.archive.success',
              },
              { archive }
            ),
            action: allowUndo
              ? {
                  label: intl.formatMessage({
                    id: 'admin/promotions.list.actions.undo',
                  }),
                  onClick: () => {
                    track(archive ? 'Undo Archive' : 'Undo Unarchive')

                    updateState({
                      id,
                      archive: !archive,
                      allowUndo: false,
                    })
                  },
                }
              : undefined,
          })
          queryClient.refetchQueries({
            queryKey: ['active-promotions'],
          })
          queryClient.refetchQueries({
            queryKey: ['archived-promotions'],
          })
        },
        onError: () =>
          showToast({
            variant: 'critical',
            message: intl.formatMessage(
              { id: 'admin/promotions.list.actions.archive.error' },
              { archive }
            ),
          }),
      }
    )
  }

  return {
    archive: (id: string) => updateState({ id, archive: true }),
    unarchive: (id: string) => updateState({ id, archive: false }),
  }
}
